import React from "react";
import { HatGlasses } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Props for the SensitiveChatButton component
 */
interface SensitiveChatButtonProps {
  /** Whether sensitive chat mode is currently active */
  isSensitive: boolean;
  /** Callback function called when the button is toggled */
  onToggle: () => void;
  /** Whether the button is disabled */
  disabled: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Whether to hide text label on mobile (icon only). Defaults to false. */
  hideTextOnMobile?: boolean;
}

/**
 * SensitiveChatButton component displays a toggle button for enabling/disabling
 * sensitive chat mode. Shows different states: active (blue background), inactive (white),
 * and disabled. Supports icon-only mode on mobile when hideTextOnMobile is true.
 * 
 * @component
 * @param {SensitiveChatButtonProps} props - Component props
 * @param {boolean} props.isSensitive - Whether sensitive mode is active
 * @param {() => void} props.onToggle - Callback fired when button is clicked
 * @param {boolean} props.disabled - Whether button is disabled
 * @param {string} [props.className] - Additional CSS classes
 * @param {boolean} [props.hideTextOnMobile=false] - Hide text on mobile (icon only)
 * @returns {JSX.Element} Toggle button for sensitive chat mode
 * 
 * @example
 * ```tsx
 * <SensitiveChatButton 
 *   isSensitive={true}
 *   onToggle={handleToggle}
 *   disabled={false}
 * />
 * ```
 */
export const SensitiveChatButton: React.FC<SensitiveChatButtonProps> = ({
  isSensitive,
  onToggle,
  disabled,
  className,
  hideTextOnMobile = false,
}) => {
  if (hideTextOnMobile) {
    return (
      <button
        className={cn(
          "rounded-full border-2 p-0.5 flex items-center justify-center w-6 h-6",
          isSensitive
            ? "bg-primary-500 border-primary-500"
            : "bg-white border-primary-500",
          !disabled && !isSensitive && "hover:bg-gray-50 cursor-pointer",
          !disabled && isSensitive && "hover:bg-primary-600 cursor-pointer",
          className
        )}
        onClick={!disabled ? onToggle : undefined}
        aria-label={
          disabled && isSensitive
            ? "Sensitive chat"
            : isSensitive
            ? "Turn off sensitive chat"
            : "Turn on sensitive chat"
        }
      >
        <HatGlasses
          size={20}
          className={isSensitive ? "text-white" : "text-primary-500"}
        />
      </button>
    );
  }

  return (
    <div
      className={cn(
        "flex items-center justify-center gap-2 rounded-full border px-3 py-1 shadow-sm",
        isSensitive
          ? "bg-primary-500 border-primary-500"
          : "bg-white border-gray-300",
        !disabled && !isSensitive && "hover:bg-gray-50 cursor-pointer",
        !disabled && isSensitive && "hover:bg-primary-600 cursor-pointer",
        className
      )}
      onClick={!disabled ? onToggle : undefined}
      role={!disabled ? "button" : undefined}
      tabIndex={!disabled ? 0 : undefined}
      onKeyDown={(e) => {
        if (!disabled && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onToggle();
        }
      }}
    >
      <HatGlasses
        size={16}
        className={isSensitive ? "text-white" : "text-primary-500"}
      />
      <span className={cn(
        "text-sm font-medium",
        isSensitive ? "text-white" : "text-primary-500"
      )}>
        {disabled && isSensitive
          ? "Sensitive chat"
          : isSensitive
          ? "Turn off sensitive chat"
          : "Turn on sensitive chat"}
      </span>
    </div>
  );
};

